import { displayLabel } from '../../utils/displayLabels';
import { useDynamicsRecordLink } from '../../hooks/useDynamicsRecordLink';
import { useDynamicsPage } from '../../hooks/useDynamicsPage';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Tag, AlertCircle, Check, X, ChevronRight, PenLine, RotateCcw, CheckCircle2, ListChecks, User, Search, PackageCheck } from 'lucide-react';
import backendApi from '../../services/backendApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ClientPage from '../common/ClientPage';
import Pagination from '../common/Pagination';
import useAnimatedModalClose from '../../hooks/useAnimatedModalClose';
import '../../styles/deliveries.css';

const SIGNATURE_FIELD = 'x_studio_signature_lectronique_1';
const DATE_SIGNATURE_FIELD = 'x_studio_date_signature';
const US_FIELD = 'x_studio_user_stories';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatValue(value, fieldDef, compact = false) {
  if (value === false || value === null || value === undefined) {
    return <span className="text-gray-300">—</span>;
  }
  if (fieldDef.type === 'binary') return null; // handled separately
  switch (fieldDef.type) {
    case 'boolean':
      return value
        ? <Check className="w-4 h-4 text-green-500 inline" />
        : <X className="w-4 h-4 text-red-400 inline" />;
    case 'date':
    case 'datetime':
      return formatDate(value);
    case 'many2one':
      return Array.isArray(value) ? value[1] : value;
    case 'selection': {
      const match = (fieldDef.selection || []).find(([key]) => key === value);
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
          {match ? match[1] : value}
        </span>
      );
    }
    case 'text':
      if (compact) {
        const s = String(value);
        return s.length > 60 ? s.slice(0, 60) + '…' : s;
      }
      return <p className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">{String(value)}</p>;
    default:
      return String(value);
  }
}

// ─── Colonnes tableau ────────────────────────────────────────────────────────

const VISIBLE_FIELDS_ORDER = ['projet', 'version', 'date', 'statut', 'etat', 'responsable', 'description'];
const EXCLUDED_STRINGS = [
  'last update', 'last updated',
  'kanban',
  'display name',
  'activity',
  'sequence',
  'attachment',    // pièces jointes
  'sms',           // sms delivery error, coût sms
  'cout',          // coût
  'actif',         // actif / active
  'active',
  'couleur',       // couleur
  'color',
  'priorite eleve', // priorité élevée
  'priorite',      // priorité
  'interlocuteur', // partner_id
  'partner',
];

function normalize(str) {
  return (str || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

function fieldOrder(fieldDef) {
  const label = normalize(fieldDef.string);
  const idx = VISIBLE_FIELDS_ORDER.findIndex(v => label.includes(v) || v.includes(label));
  return idx === -1 ? 999 : idx;
}

function sortColumns(fields) {
  return Object.entries(fields)
    .filter(([fieldName, fieldDef]) => {
      if (fieldName === 'id') return false;
      if (fieldDef.type === 'one2many') return false;
      if (fieldDef.type === 'binary') return false;
      const label = normalize(fieldDef.string);
      if (EXCLUDED_STRINGS.some(e => label.includes(e))) return false;
      return true;
    })
    .sort(([, a], [, b]) => fieldOrder(a) - fieldOrder(b));
}

function deliveryLabel(value) {
  return Array.isArray(value) ? value[1] : value;
}

function meaningfulDeliveryValue(value) {
  const label = deliveryLabel(value);
  return label && label !== '/' ? label : null;
}

function parseDeliveryDate(value) {
  if (!value) return null;
  const parsed = new Date(typeof value === 'string' ? value.replace(' ', 'T') : value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// ─── Helpers US ──────────────────────────────────────────────────────────────

const STAGE_COLORS = {
  'backlog':           'bg-gray-100 text-gray-600',
  'raffinage':         'bg-violet-100 text-violet-700',
  'en cours':          'bg-blue-100 text-blue-700',
  'en test':           'bg-yellow-100 text-yellow-700',
  'tests à valider':   'bg-orange-100 text-orange-700',
  'test client':       'bg-orange-100 text-orange-700',
  'done':              'bg-green-100 text-green-700',
  'refusé':            'bg-red-100 text-red-600',
};

function stageColor(stageName) {
  const n = (stageName || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const key = Object.keys(STAGE_COLORS).find(k => n.includes(k));
  return key ? STAGE_COLORS[key] : 'bg-gray-100 text-gray-600';
}

// ─── Section User Stories ─────────────────────────────────────────────────────

const UserStoriesSection = ({ version, relatedData }) => {
  const usIds = version[US_FIELD];
  if (!Array.isArray(usIds) || usIds.length === 0) return null;

  const data = relatedData[US_FIELD] || { records: [] };
  const records = data.records.filter(r => usIds.includes(r.id));

  if (records.length === 0) return null;

  return (
    <div className="border-t border-gray-100 pt-5 mt-2">
      <div className="flex items-center gap-2 mb-4">
        <ListChecks className="w-4 h-4 text-indigo-500" />
        <h3 className="text-sm font-bold text-gray-700">User Stories</h3>
        <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">
          {records.length}
        </span>
      </div>

      <div className="border border-gray-100 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Titre</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Statut</th>
              <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">SP</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Jalon</th>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">Assigné</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {records.map(us => {
              const stageName = Array.isArray(us.stage_id) ? us.stage_id[1] : (us.stage_id || '—');
              const jalonName = Array.isArray(us.milestone_id) ? us.milestone_id[1] : (us.milestone_id || null);
              const sp = us.x_studio_story_points;
              const assignees = Array.isArray(us.user_ids) ? us.user_ids : [];

              return (
                <tr key={us.id} className="hover:bg-indigo-50/30 transition-colors">
                  <td className="px-4 py-3 text-gray-800 font-medium max-w-xs">
                    <span className="line-clamp-2">{us.name || '—'}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${stageColor(stageName)}`}>
                      {displayLabel(stageName)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {sp ? (
                      <span className="inline-block px-2 py-0.5 text-xs font-bold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {sp}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                    {jalonName ? (
                      <span className="inline-block px-2 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-100 font-medium">
                        {jalonName}
                      </span>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {assignees.length > 0 ? (
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {assignees.length > 1 ? `${assignees.length} personnes` : (Array.isArray(assignees[0]) ? assignees[0][1] : '—')}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-300">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Pad de signature ────────────────────────────────────────────────────────

const SignaturePad = ({ onSave, onCancel, saving }) => {
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const lastPos = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width || 560;
    canvas.height = 180;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f8f8ff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    isDrawing.current = true;
    lastPos.current = pos;
    ctx.beginPath();
    ctx.arc(pos.x, pos.y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = '#1e1b4b';
    ctx.fill();
    setIsEmpty(false);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1e1b4b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDrawing = () => { isDrawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#f8f8ff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const save = () => {
    if (isEmpty) return;
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL('image/png');
    const base64 = dataURL.split(',')[1];
    onSave(base64);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">Dessinez votre signature dans la zone ci-dessous</p>
      <div className="border-2 border-dashed border-indigo-200 rounded-xl overflow-hidden bg-[#f8f8ff]">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair touch-none block"
          style={{ height: 180 }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
      </div>
      <div className="flex items-center gap-2 justify-between">
        <button
          onClick={clear}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" /> Effacer
        </button>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={save}
            disabled={isEmpty || saving}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            {saving ? (
              <span className="flex items-center gap-1.5"><span className="w-3.5 h-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block" /> Enregistrement…</span>
            ) : (
              <><Check className="w-3.5 h-3.5" /> Valider la signature</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Résultat signature (bas du document) ────────────────────────────────────

const SignatureResult = ({ version }) => {
  const sig = version[SIGNATURE_FIELD];
  if (!sig) return null;

  const rawDate = version[DATE_SIGNATURE_FIELD];
  const dateLabel = rawDate
    ? new Date(rawDate).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="border-t border-gray-100 pt-5 mt-2">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5 text-green-500" /> Signature électronique
        </p>
        {dateLabel && (
          <span className="text-xs text-gray-400">Signé le {dateLabel}</span>
        )}
      </div>
      <div className="border border-gray-100 rounded-xl p-4 bg-gray-50 inline-block">
        <img
          src={`data:image/png;base64,${sig}`}
          alt="Signature"
          className="max-h-28 object-contain"
        />
      </div>
    </div>
  );
};

// ─── Popup de signature (au-dessus de la modal) ───────────────────────────────

const SignaturePopup = ({ onSave, onClose, saving, error }) => {
  const modalExit = useAnimatedModalClose(onClose, { disabled: saving });
  const saveAndClose = async (...args) => {
    const saved = await onSave(...args);
    if (saved !== false) modalExit.close();
  };

  return <div className={`${modalExit.modalClassName} fixed inset-0 z-[60] flex items-center justify-center p-4`}>
    <div className="fixed inset-0 bg-black/40" onClick={modalExit.close} />
    <div className="dashboard-modal__surface relative bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-modal-in">
      {/* Header popup */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <PenLine className="w-4 h-4 text-indigo-600" />
          <h3 className="text-sm font-bold text-gray-800">Signature électronique</h3>
        </div>
        <button
          onClick={modalExit.close}
          className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="p-5">
        {error && (
          <p className="text-sm text-red-600 mb-3 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </p>
        )}
        <SignaturePad onSave={saveAndClose} onCancel={modalExit.close} saving={saving} />
      </div>
    </div>
  </div>;
};

// ─── Modal de détails ────────────────────────────────────────────────────────

const VersionModal = ({ version, columns, one2manyFields, relatedData, onClose, onSigned }) => {
  const [showPad, setShowPad] = useState(false);
  const [saving, setSaving] = useState(false);
  const [signError, setSignError] = useState(null);
  const modalExit = useAnimatedModalClose(onClose);

  if (!version) return null;
  const name = version.x_name || version.display_name || `Livraison #${version.id}`;
  const isSigned = !!version[SIGNATURE_FIELD];

  const handleSave = async (base64) => {
    setSaving(true);
    setSignError(null);
    try {
      await backendApi.signLivraison(version.id, base64);
      onSigned(version.id, base64);
      return true;
    } catch (err) {
      setSignError(err.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`client-dialog ${modalExit.modalClassName} fixed inset-0 z-[60] flex items-center justify-center p-4`}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={modalExit.close} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[95vh] animate-modal-in">

        <div className="flex-none bg-gradient-to-r from-indigo-600 to-indigo-500 px-6 py-5 rounded-t-2xl">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Tag className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-indigo-200 font-medium uppercase tracking-wide">Livraison</p>
                <h2 className="text-lg font-bold text-white truncate">{name}</h2>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isSigned ? (
                <>
                  <span className="flex items-center gap-1 text-xs bg-green-500/20 text-green-100 border border-green-400/30 px-2.5 py-1 rounded-full font-medium">
                    <CheckCircle2 className="w-3 h-3" /> Signé
                  </span>
                  <button
                    onClick={() => setShowPad(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors"
                  >
                    <PenLine className="w-3.5 h-3.5" /> Re-signer
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowPad(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-indigo-700 bg-white hover:bg-indigo-50 rounded-xl shadow-sm transition-colors"
                >
                  <PenLine className="w-4 h-4" />
                  Signer cette livraison
                </button>
              )}
              <button
                onClick={modalExit.close}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Champs scalaires */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-5">
            {columns.map(([fieldName, fieldDef]) => {
              const value = version[fieldName];
              if (value === false || value === null || value === undefined) return null;
              const isText = fieldDef.type === 'text';
              return (
                <div key={fieldName} className={isText ? 'col-span-2' : ''}>
                  <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-1">
                    {displayLabel(fieldDef.string || fieldName)}
                  </p>
                  <div className="text-sm text-gray-800 font-medium">
                    {formatValue(value, fieldDef, false)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Section User Stories */}
          <UserStoriesSection version={version} relatedData={relatedData} />

          {/* Autres champs one2many (hors US) */}
          {one2manyFields.filter(([fn]) => fn !== US_FIELD).map(([fieldName, fieldDef]) => {
            const ids = version[fieldName];
            if (!Array.isArray(ids) || ids.length === 0) return null;
            const data = relatedData[fieldName] || { fields: {}, records: [] };
            const relatedRecords = data.records.filter(r => ids.includes(r.id));
            const allCols = Object.entries(data.fields).filter(([k]) => k !== 'id');

            return (
              <div key={fieldName}>
                <div className="flex items-center gap-2 mb-3">
                  <h3 className="text-sm font-bold text-gray-700">{displayLabel(fieldDef.string || fieldName)}</h3>
                  <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full font-semibold">
                    {relatedRecords.length}
                  </span>
                </div>
                {relatedRecords.length === 0 ? (
                  <p className="text-sm text-gray-400 italic">Aucun enregistrement</p>
                ) : (
                  <div className="border border-gray-100 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            {allCols.map(([fn, fd]) => (
                              <th key={fn} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                {displayLabel(fd.string || fn)}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {relatedRecords.map(record => (
                            <tr key={record.id} className="hover:bg-indigo-50/30 transition-colors">
                              {allCols.map(([fn, fd]) => {
                                const isText = fd.type === 'text' || (fd.string || '').toLowerCase().includes('descri');
                                return (
                                  <td key={fn} className={`px-3 py-2.5 text-gray-700 align-top ${isText ? 'whitespace-pre-wrap break-words max-w-sm' : 'whitespace-nowrap'}`}>
                                    {formatValue(record[fn], fd, !isText)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Signature — résultat seul en bas du document */}
          <SignatureResult version={version} />
        </div>
      </div>

      {/* Popup de signature indépendant (z-index supérieur) */}
      {showPad && (
        <SignaturePopup
          onSave={handleSave}
          onClose={() => { setShowPad(false); setSignError(null); }}
          saving={saving}
          error={signError}
        />
      )}

      <style>{`
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.95) translateY(8px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in { animation: modal-in 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>
    </div>
  );
};

// ─── Page principale ─────────────────────────────────────────────────────────

const LivraisonsPage = () => {
  const [versions, setVersions] = useState([]);
  const [fields, setFields] = useState({});
  const [relatedData, setRelatedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [signatureFilter, setSignatureFilter] = useState('all');
  const [selectedVersion, setSelectedVersion] = useState(null);

  const activeProjectId = useMemo(() => {
    const raw = localStorage.getItem('selected_project_id');
    return raw ? parseInt(raw, 10) : null;
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    backendApi.getLivraisons(activeProjectId)
      .then(data => {
        if (cancelled) return;
        setVersions(data.versions || []);
        setFields(data.fields || {});
        setRelatedData(data.relatedData || {});
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [activeProjectId]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') setSelectedVersion(null); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Mise à jour locale de la signature après signature réussie
  const handleSigned = useCallback((id, base64) => {
    const today = new Date().toISOString().split('T')[0];
    const patch = { [SIGNATURE_FIELD]: base64, [DATE_SIGNATURE_FIELD]: today };
    setVersions(prev => prev.map(v => v.id === id ? { ...v, ...patch } : v));
    setSelectedVersion(prev => prev?.id === id ? { ...prev, ...patch } : prev);
  }, []);

  const columns = useMemo(() => sortColumns(fields), [fields]);
  const one2manyFields = useMemo(() => Object.entries(fields).filter(([, def]) => def.type === 'one2many'), [fields]);

  const fieldKeys = useMemo(() => {
    const entries = Object.entries(fields);
    const byLabel = predicate => entries.find(([, definition]) => predicate(normalize(definition.string)))?.[0];
    const dateCandidates = entries.filter(([name, definition]) => name !== DATE_SIGNATURE_FIELD
      && normalize(definition.string).includes('date'));
    const preferredDate = dateCandidates.find(([, definition]) => {
      const label = normalize(definition.string);
      return label.includes('livraison') || label.includes('version');
    })?.[0] || dateCandidates[0]?.[0];
    return {
      date: preferredDate,
      project: byLabel(label => label.includes('projet')),
      status: byLabel(label => label.includes('statut') || label.includes('etat')),
      responsible: byLabel(label => label.includes('responsable')),
    };
  }, [fields]);

  const signatureCounts = useMemo(() => versions.reduce((counts, version) => {
    counts[version[SIGNATURE_FIELD] ? 'signed' : 'unsigned'] += 1;
    return counts;
  }, { signed: 0, unsigned: 0 }), [versions]);

  const filteredVersions = useMemo(() => {
    const query = normalize(searchQuery);
    return versions.filter(version => {
      const matchesSearch = !query || normalize(version.x_name || version.display_name).includes(query);
      const signed = !!version[SIGNATURE_FIELD];
      const matchesSignature = signatureFilter === 'all'
        || (signatureFilter === 'signed' ? signed : !signed);
      return matchesSearch && matchesSignature;
    });
  }, [versions, searchQuery, signatureFilter]);

  useEffect(() => { setPage(1); }, [searchQuery, signatureFilter]);
  const pageCount = Math.max(1, Math.ceil(filteredVersions.length / 10));
  const currentPage = Math.min(page, pageCount);
  const visibleRecords = filteredVersions.slice((currentPage - 1) * 10, currentPage * 10);

  const groupedRecords = useMemo(() => {
    const ordered = [...visibleRecords].sort((a, b) => {
      const aDate = parseDeliveryDate(a[fieldKeys.date]);
      const bDate = parseDeliveryDate(b[fieldKeys.date]);
      return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
    });
    return ordered.reduce((groups, version) => {
      const date = parseDeliveryDate(version[fieldKeys.date]);
      const key = date ? `${date.getFullYear()}-${date.getMonth()}` : 'undated';
      let group = groups.find(item => item.key === key);
      if (!group) {
        group = {
          key,
          label: date
            ? date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^./, character => character.toUpperCase())
            : 'Date à préciser',
          records: [],
        };
        groups.push(group);
      }
      group.records.push(version);
      return groups;
    }, []);
  }, [visibleRecords, fieldKeys.date]);

  const handleRowClick = useCallback((v) => {
    setSelectedVersion(prev => prev?.id === v.id ? null : v);
  }, []);

  useDynamicsRecordLink(versions, setSelectedVersion);
  useDynamicsPage({path:'/dashboard/livraisons',title:'Livraisons',kind:'versions',recordIds:(selectedVersion ? [selectedVersion] : filteredVersions).map(t => t.id),filters:{search:searchQuery},ready:!loading});

  const headerActions = <div className="delivery-toolbar" aria-label="Filtres des livraisons">
    <div className="delivery-segments" role="group" aria-label="Signature">
      {[
        ['all', 'Toutes', versions.length],
        ['signed', 'Signées', signatureCounts.signed],
        ['unsigned', 'À signer', signatureCounts.unsigned],
      ].map(([value, label, count]) => <button
        key={value}
        type="button"
        aria-pressed={signatureFilter === value}
        onClick={() => setSignatureFilter(value)}
      >{label} · {count}</button>)}
    </div>
    <label className="delivery-search">
      <span className="sr-only">Rechercher une livraison</span>
      <Search size={15} aria-hidden="true" />
      <input type="search" placeholder="Rechercher" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} />
    </label>
  </div>;

  return (
    <ClientPage title="Livraisons" subtitle={loading ? 'Chargement…' : 'Consultez vos livraisons et leurs signatures.'} actions={headerActions}>
      <div key={`${signatureFilter}|${searchQuery}|${currentPage}`} className="client-records delivery-records filter-results-motion">
        {loading && (
          <div className="flex items-center justify-center h-40"><LoadingSpinner /></div>
        )}

        {!loading && error && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-medium">Erreur de chargement</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && columns.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Tag className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Aucune livraison disponible</p>
          </div>
        )}

        {!loading && !error && columns.length > 0 && filteredVersions.length === 0 && signatureFilter !== 'signed' && (
          <div className="delivery-empty">
            <Tag size={22} aria-hidden="true" />
            <div><strong>Aucune livraison trouvée</strong><p>{searchQuery ? 'Modifiez votre recherche pour afficher d’autres résultats.' : 'Les prochaines livraisons apparaîtront ici.'}</p></div>
          </div>
        )}

        {!loading && !error && columns.length > 0 && groupedRecords.map(group => (
          <section key={group.key} className="delivery-month" aria-labelledby={`delivery-month-${group.key}`}>
            <h2 id={`delivery-month-${group.key}`}>{group.label}</h2>
            <div className="delivery-timeline" role="table" aria-label={`Livraisons — ${group.label}`}>
              {group.records.map(version => {
                const date = parseDeliveryDate(version[fieldKeys.date]);
                const project = meaningfulDeliveryValue(version[fieldKeys.project]);
                const status = meaningfulDeliveryValue(version[fieldKeys.status]);
                const responsible = meaningfulDeliveryValue(version[fieldKeys.responsible]);
                const isSigned = !!version[SIGNATURE_FIELD];
                const meta = [project, status, responsible].filter(Boolean);
                return <div
                  key={version.id}
                  role="row"
                  tabIndex={0}
                  aria-label={version.x_name || version.display_name || `Livraison ${version.id}`}
                  onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') handleRowClick(version); }}
                  onClick={() => handleRowClick(version)}
                  className={`delivery-timeline__row ${selectedVersion?.id === version.id ? 'is-selected' : ''}`}
                >
                  <div className="delivery-date" aria-label={date ? date.toLocaleDateString('fr-FR') : 'Date à préciser'}>
                    <span>{date ? date.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '') : '—'}</span>
                    <strong>{date ? date.getDate() : '—'}</strong>
                  </div>
                  <div className="delivery-summary">
                    <strong>{version.x_name || version.display_name || `Livraison #${version.id}`}</strong>
                    <div className="delivery-meta">
                      {meta.length ? meta.map((item, index) => <span key={`${item}-${index}`}>{item}</span>) : <span>Détails disponibles dans la livraison</span>}
                    </div>
                  </div>
                  <span className={`delivery-signature ${isSigned ? 'is-signed' : 'is-pending'}`}>
                    {isSigned ? <><CheckCircle2 size={13} /> Signée</> : <><PenLine size={13} /> À signer</>}
                  </span>
                  <ChevronRight className="delivery-chevron" size={17} aria-hidden="true" />
                </div>;
              })}
            </div>
          </section>
        ))}

        {!loading && !error && columns.length > 0 && versions.length > 0 && signatureCounts.signed === 0 && signatureFilter !== 'unsigned' && !searchQuery && (
          <div className="delivery-signed-empty">
            <span><PackageCheck size={17} aria-hidden="true" /></span>
            <div><strong>Aucune livraison signée pour l’instant</strong><p>Les livraisons validées par signature apparaîtront ici.</p></div>
          </div>
        )}
      </div>

      {pageCount > 1 && <Pagination currentPage={currentPage} totalPages={pageCount} totalItems={filteredVersions.length} itemsPerPage={10} onPageChange={setPage} />}
      <VersionModal
        version={selectedVersion}
        columns={columns}
        one2manyFields={one2manyFields}
        relatedData={relatedData}
        onClose={() => setSelectedVersion(null)}
        onSigned={handleSigned}
      />
    </ClientPage>
  );
};

export default LivraisonsPage;
